import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { UserFieldDisplay } from "./UserFieldDisplay";
import { MultiUserFieldDisplay } from "./MultiUserFieldDisplay";
import { AttachmentsFieldDisplay } from "./AttachmentsFieldDisplay";
import type { CustomFieldValues } from "@forgepoint/shared";

/** Props for the ReadOnlyValue component. */
interface ReadOnlyValueProps {
  value: CustomFieldValues[string];
  format?: string;
  type: "string" | "number" | "array";
  inline: boolean;
}

/**
 * Renders the display value for a read-only custom field.
 * Handles user resolution, date formatting, and empty state.
 * @param value - The raw field value
 * @param format - Optional format hint
 * @param type - The primitive type
 * @param inline - Whether to render in the large inline style or compact standalone style
 */
export function ReadOnlyValue({
  value,
  format: fieldFormat,
  type,
  inline,
}: ReadOnlyValueProps) {
  const textClass = inline ? "text-2xl font-bold" : "text-sm font-medium";
  const emptyClass = inline
    ? "text-2xl font-bold text-muted-foreground/30"
    : "text-sm text-muted-foreground/50";

  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return <p className={emptyClass}>&mdash;</p>;
  }

  if (fieldFormat === "fpUser" && type === "array" && Array.isArray(value)) {
    return <MultiUserFieldDisplay userIds={value} inline={inline} />;
  }

  if (fieldFormat === "fpAsset" && type === "array" && Array.isArray(value)) {
    return <AttachmentsFieldDisplay assetIds={value} />;
  }

  if (type === "array" && Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((option) => (
          <Badge key={option} variant="secondary" className="font-normal">
            {option}
          </Badge>
        ))}
      </div>
    );
  }

  if (fieldFormat === "fpUser" && typeof value === "string") {
    return <UserFieldDisplay userId={value} inline={inline} />;
  }

  if (fieldFormat === "date" && typeof value === "string") {
    return <p className={textClass}>{formatDateValue(value)}</p>;
  }

  if (type === "number") {
    const suffix = fieldFormat === "percentage" ? "%" : "";
    return (
      <p className={`${textClass} tabular-nums`}>
        {String(value)}
        {suffix}
      </p>
    );
  }

  return (
    <p className={`${textClass} whitespace-pre-line break-words`}>
      {String(value)}
    </p>
  );
}

/**
 * Formats a date string value for display.
 * @param value - An ISO date string
 * @returns Formatted date string (e.g. "May 11, 2026")
 */
function formatDateValue(value: string): string {
  try {
    return format(new Date(value), "MMM d, yyyy");
  } catch {
    return value;
  }
}
